import { useEffect, useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getFirebaseClient,
  useCrypto,
  consultationConditionLabel,
  parseConsultationDoc,
  parseConsultationDetail,
  groupFormDataIntoSections,
  ConsultationPdfButton,
  ConsultationSignatures,
  type ConsultationDoc,
  type ConsultationDecryptedDetail,
} from "@workspace/admin-ui";
import { doc, getDoc } from "firebase/firestore";
import { PageHeader } from "@/components/AppShell";

// Task #438 — Pharmacy Admin Consultation detail. Plaintext fields render
// immediately from the Firestore doc; the sensitive `encryptedDetail` blob
// is decrypted client-side once the chain key is loaded (see
// packages/admin-ui/src/CryptoContext.tsx — the same E2EE scheme used
// elsewhere in the app).
export default function ConsultationDetailPage() {
  const params = useParams<{ id: string }>();
  const { db } = getFirebaseClient();
  const { isReady, decryptString } = useCrypto();

  const docQ = useQuery<ConsultationDoc | null>({
    queryKey: ["consultation", params.id],
    queryFn: async () => {
      const snap = await getDoc(doc(db, "consultations", params.id));
      return snap.exists() ? parseConsultationDoc(snap.id, snap.data()) : null;
    },
  });

  const [detail, setDetail] = useState<ConsultationDecryptedDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    setDetail(null);
    setDetailError(null);
    if (!docQ.data || !isReady) return;
    // Guard against a stale decrypt resolving after unmount or after the
    // user has already navigated to a different consultation (params.id
    // changed) — without this, a slow decrypt for consultation A can land
    // after we've moved on to consultation B and overwrite its view, or
    // resolve into a component that already unmounted.
    let cancelled = false;
    decryptString(docQ.data.encryptedDetail)
      .then((plaintext) => {
        if (cancelled) return;
        const parsed = parseConsultationDetail(plaintext);
        if (!parsed) {
          setDetailError("Could not parse decrypted consultation detail.");
        } else {
          setDetail(parsed);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setDetailError(err instanceof Error ? err.message : "Decryption failed");
      });
    return () => {
      cancelled = true;
    };
  }, [docQ.data, isReady, decryptString]);

  if (docQ.isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!docQ.data) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Consultation not found.</Typography>
        <Button
          component={Link}
          href="/consultations"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Consultations
        </Button>
      </Box>
    );
  }

  const c = docQ.data;

  return (
    <>
      <PageHeader
        title={c.patientName || "Unnamed patient"}
        description={consultationConditionLabel(c.condition)}
        actions={
          <Button
            component={Link}
            href="/consultations"
            startIcon={<ArrowBackIcon />}
            size="small"
          >
            Back
          </Button>
        }
      />
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 780 }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Overview
          </Typography>
          <Stack spacing={1}>
            <InfoRow
              label="Pharmacist"
              value={`${c.pharmacistName || "—"}${
                c.pharmacistPSINumber ? ` (PSI ${c.pharmacistPSINumber})` : ""
              }`}
            />
            <InfoRow label="Condition" value={consultationConditionLabel(c.condition)} />
            <InfoRow
              label="Completed"
              value={c.completedAt ? new Date(c.completedAt).toLocaleString() : "—"}
            />
            <InfoRow
              label="Created"
              value={c.createdAt ? new Date(c.createdAt).toLocaleString() : "—"}
            />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <ConsultationPdfButton
            consultationId={c.id}
            hasPdf={!!c.cloudPaths?.pdf}
            pdfPassword={c.pharmacistPSINumber}
          />
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Patient & consultation detail
          </Typography>
          {!isReady ? (
            <Typography variant="body2" color="text.secondary">
              Waiting for the chain encryption key to load…
            </Typography>
          ) : detailError ? (
            <Typography variant="body2" color="error">
              {detailError}
            </Typography>
          ) : !detail ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CircularProgress size={18} />
            </Box>
          ) : (
            <Stack spacing={1.5}>
              <InfoRow label="Date of birth" value={detail.patientDOB || "—"} />
              <InfoRow
                label="Age"
                value={detail.patientAge != null ? String(detail.patientAge) : "—"}
              />
              {(() => {
                const sections = groupFormDataIntoSections(c.condition, detail.formData);
                return sections.length > 0
                  ? sections.map((section) => (
                      <Box key={section.id}>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 1, display: "block" }}
                        >
                          {section.title}
                        </Typography>
                        <Stack spacing={0.75}>
                          {section.rows.map((row) => (
                            <InfoRow key={row.fieldId} label={row.label} value={row.value} />
                          ))}
                        </Stack>
                      </Box>
                    ))
                  : null;
              })()}
              <Box>
                <Divider sx={{ my: 1.5 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Signatures
                </Typography>
                <ConsultationSignatures detail={detail} />
              </Box>
            </Stack>
          )}
        </Paper>
      </Box>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between" }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "70%" }}>
        {value}
      </Typography>
    </Stack>
  );
}
